import { Events, pubsub } from '@service';
import { BranchLocation, LoadingState, SlottedTemplate } from '@model';
import shopifyService from '@service/shopify';
import { InventoryLookupRequest, InventoryLookupResponse } from '@model/erp';
import erpService from '@service/erp';

/**
 * @typedef {import('@model').BranchLocation} BranchLocation
 * @typedef {import('@model/erp').InventoryLookupRequest} InventoryLookupRequest
 */
export class ProductDetailInstorePickupCard extends SlottedTemplate {
  /** @type {LoadingState} */
  loadingState = new LoadingState();

  /** @type {ShopifyService} */
  shopify;

  /** @type {InventoryLookupRequest} */
  inventoryLookupRequest = InventoryLookupRequest.forOne();

  /** @type {boolean} */
  isAvailable = false;

  /** @type {string} */
  locationName = '';

  /** @type {number} */
  quantityAvailable = 0;

  constructor() {
    super();
    void this.init();
  }

  /**
   * Checks if there is anything in storage and applies it if it is available.
   * @returns {Promise<void>}
   */
  async initStoredLocationInformation() {
    const storedLocationInformation = sessionStorage.getItem('branchLocationInformation');
    if (storedLocationInformation) {
      const branchLocation = Object.assign(new BranchLocation(), JSON.parse(storedLocationInformation));
      if (branchLocation.city && branchLocation.state) {
        void this.onBranchLocationResolved(branchLocation);
      }
    }
  }

  async initShopifyProductMetadata() {
    const variant = await this.shopify.firstProductVariantOrNull;
    if (!variant) {
      return;
    }

    const sku = variant.sku;
    if (sku) {
      this.inventoryLookupRequest.sku = sku;
    }

    if (this.inventoryLookupRequest.isValid) {
      pubsub.pub("_inventoryLookupRequested", this.inventoryLookupRequest);
    }
  }

  /**
   * Initialize the component.
   * @returns {Promise<void>}
   */
  async init() {
    this.shopify = shopifyService;

    // Subscribe to events
    pubsub.sub(Events.branchLocationResolved, this.onBranchLocationResolved.bind(this));
    pubsub.sub("_inventoryLookupRequested", this.onInventoryLookupChangeRequested.bind(this));

    // Show initial loading state
    this.render();

    // Initialize data
    void this.initStoredLocationInformation();
    void this.initShopifyProductMetadata();
  }

  /**
   * Handles the event when a user requests to change the branch location.
   * @param {Event} e
   */
  onBranchLocationChangeRequested(e) {
    e.preventDefault();
    pubsub.pub(Events.raiseZipCodeModal);
  }

  async onInventoryLookupChangeRequested() {
    this.loadingState.start();
    this.quantityAvailable = 0;
    this.render();

    const inventoryLookupResponse = await erpService.inventoryLookup(this.inventoryLookupRequest);

    // Update state from response
    const firstLocation = inventoryLookupResponse.locations[0];
    this.quantityAvailable = firstLocation?.availableQuantity ?? 0;
    this.isAvailable = this.quantityAvailable > 0;
    this.locationName = firstLocation?.locationName ?? '';

    this.loadingState.stop();
    this.render();

    // Reset request
    this.inventoryLookupRequest = InventoryLookupRequest.forOne();
  }

  /** @param {BranchLocation} branchLocation */
  async onBranchLocationResolved(branchLocation) {
    this.inventoryLookupRequest.withBranchLocation(branchLocation.id);
    this.locationName = branchLocation.name;
    // There might still be a product identifier here
    const sku = this.shopify.firstProductVariantOrNull?.sku ?? "";
    if (!!sku.trim().length) {
      this.inventoryLookupRequest.sku = sku.trim();
    }

    if (this.inventoryLookupRequest.isValid) {
      void this.onInventoryLookupChangeRequested();
    } else {
      this.render();
    }
  }

  /**
   * Renders the component based on current state
   */
  render() {

    // Determine which slot to show
    let slotName;
    if (this.loadingState.isLoading) {
      slotName = 'loading';
    } else if (this.isAvailable) {
      slotName = 'available';
    } else {
      slotName = 'unavailable';
    }

    // Render the appropriate slot
    this.renderSlot(slotName);

    // Update dynamic content in the rendered slot
    this.updateDynamicContent();
  }

  /**
   * Updates dynamic content (location names, quantities) in the currently rendered slot
   */
  updateDynamicContent() {
    // Update location names
    const locationNameEls = this.outputContainer.querySelectorAll('[data-location-name]');
    locationNameEls.forEach(el => {
      el.textContent = this.locationName;
    });

    // Update quantity
    const quantityEls = this.outputContainer.querySelectorAll('[data-quantity-available]');
    quantityEls.forEach(el => {
      el.textContent = String(this.quantityAvailable);
    });

    // Attach event listeners to "Change Store" buttons
    const changeButtons = this.outputContainer.querySelectorAll('[data-location-change-trigger]');
    changeButtons.forEach(btn => {
      btn.addEventListener('click', this.onBranchLocationChangeRequested.bind(this));
    });
  }

  /**
   * Registers the custom element
   */
  static register() {
    if (!customElements.get('product-detail-instore-pickup-card')) {
      customElements.define('product-detail-instore-pickup-card', ProductDetailInstorePickupCard);
    }
  }
}

ProductDetailInstorePickupCard.register();